// P:\0PJ\synforge\frontend\app\settings\workspace\page.tsx v6 2026.10.04 15:06
//   v6: 과금 안내는 TTS 토글이 켜졌을 때만 표시. 문구의 {chars}/{tokens} 를
//       /tts/price-example/ 실제 과금 값으로 치환(Synforge 토큰).
// P:\0PJ\synforge\frontend\app\settings\workspace\page.tsx v5 2026.10.04 15:00
//   v5: TTS 카드에 과금 안내 문구 추가 — 켜져 있으면 발언 저장 시 백그라운드 합성·과금,
//       회의 화면 스피커 음소거로는 과금이 멈추지 않음. 문구는 i18ntext(workspaceTts.billingNotice).
// P:\0PJ\synforge\frontend\app\settings\workspace\page.tsx v4 2026.06.28
'use client';

import { useState, useEffect } from 'react';
import { Trash2, Check, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { useLocale } from '@/contexts/LocaleContext';

// ──────────────────────────────────────────
// 공통 컴포넌트
// ──────────────────────────────────────────
function Card({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="bg-[#0f0f18] border border-white/[0.07] rounded-2xl p-6 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {desc && <p className="text-xs text-white/30 mt-0.5">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-white/50 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-white/25 mt-1">{hint}</p>}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input {...props}
      className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-purple-500/60 transition-colors"
    />
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!value)}
      className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${value ? 'bg-purple-600' : 'bg-white/10'}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
}

function SaveButton({ onClick, saved, loading }: { onClick: () => void; saved: boolean; loading?: boolean }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button onClick={onClick} disabled={loading}
        className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
      {saved && (
        <span className="flex items-center gap-1.5 text-green-400 text-xs">
          <Check size={13} /> Saved
        </span>
      )}
    </div>
  );
}

// ──────────────────────────────────────────
// 메인
// ──────────────────────────────────────────
export default function WorkspacePage() {
  // t: 다국어 문구 조회(i18ntext 가 export 한 messages/*.json 기준)
  const { setLocale, t } = useLocale();
  const [loading, setLoading] = useState(true);

  // General
  const [workspaceName, setWorkspaceName] = useState("My Workspace");
  const [timezone, setTimezone] = useState('Australia/Brisbane');
  const [language, setLanguage] = useState('en');
  const [generalSaved, setGeneralSaved] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(false);

  // Notifications
  const [notif, setNotif] = useState({
    meetingComplete: true,
    weeklyReport: true,
    memberJoined: false,
    systemUpdates: true,
  });
  const [notifSaved, setNotifSaved] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  // TTS (회의 읽어주기) 계정 기본값 - 회의 화면 로컬 토글의 초기값으로 쓰인다.
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [ttsVolume, setTtsVolume] = useState(1.0);
  const [ttsSaved, setTtsSaved] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  // v6: TTS 과금 예시(서버 계산값) — 로드 전에는 null
  const [ttsPriceEx, setTtsPriceEx] = useState<{ chars: number; tokens: number } | null>(null);

  // Danger
  const [deleteConfirm, setDeleteConfirm] = useState('');

  // ── 초기 데이터 로드 ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        const wsRes = await api.get('/workspace/settings/');
        const ws = wsRes.data;

        setWorkspaceName(ws.workspace_name || "My Workspace");
        setTimezone(ws.timezone || 'Australia/Brisbane');
        setLanguage(ws.language || 'en');
        if (ws.notifications) setNotif(ws.notifications);
        setTtsEnabled(ws.tts_enabled !== undefined ? ws.tts_enabled : true);
        setTtsVolume(ws.tts_volume !== undefined ? ws.tts_volume : 1.0);

        // 언어 쿠키 동기화
        const cookieLocale = document.cookie.split('; ').find(r => r.startsWith('locale='))?.split('=')[1];
        if (ws.language && ws.language !== cookieLocale) {
          document.cookie = `locale=${ws.language}; path=/; max-age=31536000; SameSite=Lax`;
        }
      } catch (err) {
        console.error('Failed to load workspace settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // v6: TTS 과금 예시 조회 — 실패해도 설정 화면은 그대로 동작
    api.get('/tts/price-example/')
      .then((r) => setTtsPriceEx({ chars: r.data.chars, tokens: r.data.tokens }))
      .catch((err) => console.error('Failed to load TTS price example:', err));
  }, []);

  // ── 저장 함수들 ──
  const saveGeneral = async () => {
    setGeneralLoading(true);
    try {
      await api.patch('/workspace/settings/', { workspace_name: workspaceName, timezone, language });
      document.cookie = `locale=${language}; path=/; max-age=31536000; SameSite=Lax`;
      setLocale(language); // 즉시 언어 변경
      setGeneralSaved(true);
      setTimeout(() => setGeneralSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save general settings:', err);
    } finally {
      setGeneralLoading(false);
    }
  };

  const saveNotif = async () => {
    setNotifLoading(true);
    try {
      await api.patch('/workspace/settings/', { notifications: notif });
      setNotifSaved(true);
      setTimeout(() => setNotifSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save notifications:', err);
    } finally {
      setNotifLoading(false);
    }
  };

  const saveTts = async () => {
    setTtsLoading(true);
    try {
      await api.patch('/workspace/settings/', { tts_enabled: ttsEnabled, tts_volume: ttsVolume });
      setTtsSaved(true);
      setTimeout(() => setTtsSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save TTS settings:', err);
    } finally {
      setTtsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <p className="text-white/30 text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[720px] space-y-4">

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-white">Workspace</h1>
        <p className="text-white/30 text-sm mt-0.5">Configure your workspace settings and defaults</p>
      </div>

      {/* ── General ── */}
      <Card title="General" desc="Basic workspace configuration">
        <Field label="Workspace Name">
          <Input value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Timezone">
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/60 transition-colors">
              <option value="Australia/Brisbane">Brisbane (UTC+10)</option>
              <option value="Australia/Sydney">Sydney (UTC+11)</option>
              <option value="Asia/Seoul">Seoul (UTC+9)</option>
              <option value="America/New_York">New York (UTC-5)</option>
              <option value="Europe/London">London (UTC+0)</option>
            </select>
          </Field>
          <Field label="Language">
            <select value={language} onChange={(e) => setLanguage(e.target.value)}
              className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/60 transition-colors">
              <option value="en">English</option>
              <option value="ko">한국어</option>
              <option value="ja">日本語</option>
              <option value="zh">中文 (简体)</option>
              <option value="zh-HK">粵語 (繁體)</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="pt">Português</option>
              <option value="ar">العربية</option>
              <option value="hi">हिन्दी</option>
            </select>
          </Field>
        </div>
        <SaveButton onClick={saveGeneral} saved={generalSaved} loading={generalLoading} />
      </Card>

      {/* ── Notifications ── */}
      <Card title="Notifications" desc="Choose what you want to be notified about">
        {(Object.keys(notif) as (keyof typeof notif)[]).map((key) => {
          const labels: Record<keyof typeof notif, { title: string; desc: string }> = {
            meetingComplete: { title: 'Meeting Complete', desc: 'When an AI panel meeting finishes' },
            weeklyReport: { title: 'Weekly Report', desc: 'Summary of your weekly activity' },
            memberJoined: { title: 'Member Joined', desc: 'When someone joins your workspace' },
            systemUpdates: { title: 'System Updates', desc: 'Product updates and announcements' },
          };
          return (
            <div key={key} className="flex items-center justify-between py-1">
              <div>
                <p className="text-sm text-white">{labels[key].title}</p>
                <p className="text-xs text-white/30">{labels[key].desc}</p>
              </div>
              <Toggle value={notif[key]} onChange={(v) => setNotif((p) => ({ ...p, [key]: v }))} />
            </div>
          );
        })}
        <SaveButton onClick={saveNotif} saved={notifSaved} loading={notifLoading} />
      </Card>

      {/* ── TTS (Read-aloud) ── */}
      <Card title="Read-aloud (TTS)" desc="Default voice settings for new meeting screens">
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-sm text-white">Enable read-aloud by default</p>
            <p className="text-xs text-white/30">New meeting screens open with TTS on or off</p>
          </div>
          <Toggle value={ttsEnabled} onChange={setTtsEnabled} />
        </div>
        {/* v6: TTS 과금 안내 — 토글이 켜졌을 때만 표시. {chars}/{tokens} 는 서버 실제 과금값으로 치환 */}
        {ttsEnabled && (
          <p className="text-[11px] leading-relaxed text-amber-300/80 bg-amber-500/5 border border-amber-500/15 rounded-lg px-3 py-2">
            {t("workspaceTts.billingNotice")
              .replace("{chars}", String(ttsPriceEx?.chars ?? 100))
              .replace("{tokens}", ttsPriceEx ? String(ttsPriceEx.tokens) : "…")}
          </p>
        )}
        {ttsEnabled && (
          <Field label={`Default volume — ${Math.round(ttsVolume * 100)}%`}>
            <input
              type="range" min={0} max={1} step={0.05}
              value={ttsVolume}
              onChange={(e) => setTtsVolume(parseFloat(e.target.value))}
              className="w-full accent-purple-500"
            />
          </Field>
        )}
        <SaveButton onClick={saveTts} saved={ttsSaved} loading={ttsLoading} />
      </Card>

      {/* ── Danger Zone ── */}
      <Card title="Danger Zone" desc="Irreversible actions for this workspace">
        <div className="flex items-start gap-3 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-white/40">
            Deleting the workspace will permanently remove all meetings, results, and vault data.
            Type <span className="text-white font-mono">DELETE</span> to confirm.
          </p>
        </div>
        <Field label='Type "DELETE" to confirm'>
          <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" />
        </Field>
        <button disabled={deleteConfirm !== 'DELETE'}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
          <Trash2 size={14} />
          Delete Workspace
        </button>
      </Card>

    </div>
  );
}